import { useRef, useEffect, useState } from 'react'
import Map, { Layer, MapRef, Marker, Popup, Source } from 'react-map-gl'
import bbox from '@turf/bbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import { Location, BoatStop } from '../models/boat'

type Props = {
  locations: Location[]
  stops?: BoatStop[]
}

export default function BoatTrackMap({ locations, stops = [] }: Props) {
  const mapRef = useRef<MapRef>(null)
  const [selectedStop, setSelectedStop] = useState<BoatStop | null>(null)

  // Transform locations to GeoJSON LineString
  const coordinates: [number, number][] = locations
    .map((loc) => {
      // Extract coordinates from GeoJSON point
      if (loc.location?.coordinates) {
        return [loc.location.coordinates[0], loc.location.coordinates[1]] as [
          number,
          number,
        ]
      }
      return null
    })
    .filter((coord): coord is [number, number] => coord !== null)

  const track = {
    type: 'LineString' as const,
    coordinates,
  }

  const geojson = {
    type: 'FeatureCollection' as const,
    features: [
      {
        type: 'Feature' as const,
        geometry: track,
      },
    ],
  }

  useEffect(() => {
    if (mapRef.current && coordinates.length > 0) {
      if (coordinates.length === 1) {
        mapRef.current.flyTo({
          center: coordinates[0],
          zoom: 15,
          duration: 200,
        })
      } else {
        const [minLng, minLat, maxLng, maxLat] = bbox(geojson as any)
        const isTinyArea = minLng === maxLng || minLat === maxLat

        if (isTinyArea) {
          mapRef.current.flyTo({
            center: coordinates[0],
            zoom: 15,
            duration: 200,
          })
        } else {
          mapRef.current.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat],
            ],
            {
              padding: 50,
              duration: 200,
              maxZoom: 13,
            }
          )
        }
      }
    }
  }, [coordinates.length])

  if (coordinates.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          borderRadius: '8px',
        }}
      >
        <p style={{ color: '#888' }}>
          No location data available for this period
        </p>
      </div>
    )
  }

  return (
    <div
      style={{
        width: '100%',
        height: '400px',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Map
        ref={mapRef}
        mapboxAccessToken="pk.eyJ1IjoiaGJhbmRvdiIsImEiOiJjbHVoMTZqazEya2p5MnNsMHF1c3ZicmV6In0.wcM_OYGTxxDZq8D8tnfKxA"
        initialViewState={{
          latitude: coordinates[0][1],
          longitude: coordinates[0][0],
          zoom: 8,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v9"
        attributionControl={false}
      >
        {/* Start marker */}
        <Marker
          longitude={coordinates[0][0]}
          latitude={coordinates[0][1]}
          color="green"
          anchor="bottom"
        />

        {/* End marker (if more than one location) */}
        {coordinates.length > 1 && (
          <Marker
            longitude={coordinates[coordinates.length - 1][0]}
            latitude={coordinates[coordinates.length - 1][1]}
            color="red"
            anchor="bottom"
          />
        )}

        {/* Track line */}
        <Source id="route-source" type="geojson" data={geojson}>
          <Layer
            id="route-layer"
            type="line"
            source="route-source"
            paint={{
              'line-color': '#3b82f6',
              'line-width': 3,
            }}
          />
        </Source>

        {/* Boat stops markers */}
        {stops.map((stop) => {
          if (!stop.location?.coordinates) return null
          const [lng, lat] = stop.location.coordinates
          return (
            <Marker
              key={stop.stop_id}
              longitude={lng}
              latitude={lat}
              color="orange"
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent.stopPropagation()
                setSelectedStop(stop)
              }}
              style={{ cursor: 'pointer' }}
            />
          )
        })}

        {/* Popup for selected stop */}
        {selectedStop && selectedStop.location?.coordinates && (
          <Popup
            longitude={selectedStop.location.coordinates[0]}
            latitude={selectedStop.location.coordinates[1]}
            anchor="bottom"
            onClose={() => setSelectedStop(null)}
            closeOnClick={false}
          >
            <div style={{ padding: '8px' }}>
              <strong>Boat Stop</strong>
              <div style={{ marginTop: '4px', fontSize: '12px' }}>
                <div>
                  <strong>Start:</strong>{' '}
                  {new Date(selectedStop.start_time).toLocaleString()}
                </div>
                {selectedStop.end_time && (
                  <>
                    <div>
                      <strong>End:</strong>{' '}
                      {new Date(selectedStop.end_time).toLocaleString()}
                    </div>
                    <div>
                      <strong>Duration:</strong>{' '}
                      {(() => {
                        const duration =
                          new Date(selectedStop.end_time).getTime() -
                          new Date(selectedStop.start_time).getTime()
                        const hours = Math.floor(duration / (1000 * 60 * 60))
                        const minutes = Math.floor(
                          (duration % (1000 * 60 * 60)) / (1000 * 60)
                        )
                        return `${hours}h ${minutes}m`
                      })()}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Popup>
        )}
      </Map>
    </div>
  )
}
